/**
 * Das Eingabefeld – im Panel und am Vorgang dasselbe Bauteil.
 *
 * Es kann drei Dinge: schreiben, jemanden erwähnen, eine Datei aus
 * der Ablage anhängen. Mehr gibt es nicht, und das ist Absicht.
 *
 * Anhänge sind kein Datei-Upload. Regel 2 aus TEAM-CHAT.md: Keine Datei
 * liegt in Kora. Angehängt wird ein Weg in die Ablage, im Verlauf steht
 * eine Karte mit Name und Ablageort.
 */

import { useEffect, useRef, useState } from 'react';
import { EMOJI_GRUPPEN } from './Emoji';

export interface Anhangwahl {
  dateiname: string;
  url: string;
  ort: string;
}

export function Verfasser({
  platzhalter,
  knopf,
  ablageHinweis,
  personen,
  antwortAuf,
  onAntwortWeg,
  onSenden,
  onTippt,
}: {
  platzhalter: string;
  knopf: string;
  /** „Anhänge liegen im Vorgangsordner · Angebote/26014_FE_…" */
  ablageHinweis: string;
  personen: string[];
  antwortAuf: { autor: string; text: string } | null;
  onAntwortWeg: () => void;
  onSenden: (text: string, anhang: Anhangwahl | null) => void;
  onTippt: () => void;
}) {
  const [text, setText] = useState('');
  const [anhang, setAnhang] = useState<Anhangwahl | null>(null);
  const [atOffen, setAtOffen] = useState(false);
  const [emojiOffen, setEmojiOffen] = useState(false);
  const [emojiGruppe, setEmojiGruppe] = useState(0);
  const [anhangOffen, setAnhangOffen] = useState(false);
  const [url, setUrl] = useState('');
  const feld = useRef<HTMLTextAreaElement>(null);

  /* Wer auf „antworten" klickt, will tippen – nicht erst ins Feld klicken. */
  useEffect(() => {
    if (antwortAuf) feld.current?.focus();
  }, [antwortAuf]);

  const einfuegen = (name: string) => {
    setText((t) => `${t}${t && !t.endsWith(' ') ? ' ' : ''}@${name} `);
    setAtOffen(false);
    feld.current?.focus();
  };

  const senden = () => {
    const sauber = text.trim();
    if (!sauber) return;
    onSenden(sauber, anhang);
    setText('');
    setAnhang(null);
  };

  return (
    <div className="vf">
      {antwortAuf && (
        <div className="vf-antwort">
          <span className="vf-antwort-text">
            Antwort an {antwortAuf.autor}: „{antwortAuf.text.slice(0, 60)}
            {antwortAuf.text.length > 60 ? '…' : ''}"
          </span>
          <button type="button" className="vf-x" title="Antwort verwerfen" onClick={onAntwortWeg}>
            ×
          </button>
        </div>
      )}

      {anhang && (
        <div className="vf-anhang">
          <span className="vf-anhang-name">{anhang.dateiname}</span>
          <button type="button" className="vf-x" title="Anhang entfernen" onClick={() => setAnhang(null)}>
            ×
          </button>
        </div>
      )}

      <textarea
        ref={feld}
        className="vf-feld"
        rows={2}
        value={text}
        placeholder={platzhalter}
        onChange={(e) => {
          setText(e.target.value);
          onTippt();
        }}
        onKeyDown={(e) => {
          /*
           * Enter sendet, Umschalt+Enter macht einen Absatz. Umgekehrt wäre
           * es für einen Zuruf zu umständlich – und ein Zuruf ist der Regelfall.
           */
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            senden();
          }
        }}
      />

      <div className="vf-leiste">
        <span className="vf-werkzeuge">
          {/*
            Emoji sind Standard-Unicode – dieselben Zeichen wie in WhatsApp,
            gezeichnet von der Schrift des Geräts. Vier Gruppen statt
            achthundert Zeichen, und „Arbeit" steht vorn: Ein Lastwagen wird
            hier häufiger gebraucht als ein Einhorn.
          */}
          <span className="vf-menue-huelle">
            <button
              type="button"
              className="vf-quadrat"
              title="Emoji einfügen"
              aria-expanded={emojiOffen}
              onClick={() => setEmojiOffen(!emojiOffen)}
            >
              🙂
            </button>
            {emojiOffen && (
              <span className="vf-menue vf-emoji">
                <span className="vf-emoji-reiter">
                  {EMOJI_GRUPPEN.map((g, i) => (
                    <button
                      type="button"
                      key={g.name}
                      className={`vf-emoji-reiter-knopf ${i === emojiGruppe ? 'aktiv' : ''}`}
                      onClick={() => setEmojiGruppe(i)}
                    >
                      {g.name}
                    </button>
                  ))}
                </span>
                <span className="vf-emoji-raster">
                  {EMOJI_GRUPPEN[emojiGruppe]?.zeichen.map((z, i) => (
                    <button
                      type="button"
                      key={z + i}
                      className="vf-emoji-zeichen"
                      title={z}
                      onClick={() => {
                        setText((t) => t + z);
                        feld.current?.focus();
                      }}
                    >
                      {z}
                    </button>
                  ))}
                </span>
              </span>
            )}
          </span>

          <span className="vf-menue-huelle">
            <button
              type="button"
              className="vf-quadrat"
              title="Person erwähnen – heißt „bitte lesen“, nicht „du bist dran“"
              onClick={() => setAtOffen(!atOffen)}
            >
              @
            </button>
            {atOffen && (
              <span className="vf-menue">
                {personen.map((p) => (
                  <button type="button" key={p} className="vf-menue-zeile" onClick={() => einfuegen(p)}>
                    {p}
                  </button>
                ))}
              </span>
            )}
          </span>

          <span className="vf-menue-huelle">
            <button
              type="button"
              className="vf-quadrat"
              title="Datei aus der Ablage anhängen"
              onClick={() => setAnhangOffen(!anhangOffen)}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 11.5l-8.8 8.8a5 5 0 0 1-7-7l9-9a3.3 3.3 0 0 1 4.7 4.7l-9 9a1.7 1.7 0 0 1-2.4-2.4l8.3-8.3" />
              </svg>
            </button>
            {anhangOffen && (
              <span className="vf-menue vf-menue--breit">
                <span className="vf-menue-titel">Adresse aus der Ablage einfügen</span>
                <input
                  className="vf-url"
                  value={url}
                  placeholder="Adresse der Datei im Vorgangsordner"
                  onChange={(e) => setUrl(e.target.value)}
                />
                <button
                  type="button"
                  className="bt-knopf-prim"
                  disabled={!/^https?:\/\//.test(url.trim())}
                  onClick={() => {
                    const roh = url.trim();
                    /* Der Dateiname steht am Ende des Pfades – mehr braucht die Karte nicht. */
                    const name = decodeURIComponent(roh.split('?')[0]!.split('/').pop() ?? 'Datei');
                    setAnhang({ dateiname: name, url: roh, ort: ablageHinweis });
                    setUrl('');
                    setAnhangOffen(false);
                  }}
                >
                  Anhängen
                </button>
                <span className="vf-menue-fuss">
                  Die Datei bleibt im Vorgangsordner. Kora merkt sich nur den Weg dorthin.
                </span>
              </span>
            )}
          </span>

          <span className="vf-ablage">{ablageHinweis}</span>
        </span>

        {/*
          Der Senden-Kreis (v15, 12.11): 42 px, mit Text in Folder Yellow,
          leer in der Sekundärfläche. Ein beschrifteter Knopf stand hier
          vorher – im Chat schreibt man aber mit Enter, und der Kreis sagt
          dasselbe in einem Viertel des Platzes.
        */}
        <button
          type="button"
          className="vf-senden"
          title={knopf}
          aria-label={knopf}
          disabled={!text.trim()}
          onClick={senden}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 12h15M13 6l6 6-6 6" />
          </svg>
        </button>
      </div>
    </div>
  );
}
